
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CommunityMember } from "@/types";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AddCommunityMemberDialog } from "../nurse/AddCommunityMemberDialog";
import { CommunityMemberDetails } from "./CommunityMemberDetails";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EditStaffDialog } from "./EditStaffDialog";

export function StaffManagement() {
  const { toast } = useToast();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<CommunityMember | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState<CommunityMember | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [staffToEdit, setStaffToEdit] = useState<CommunityMember | null>(null);

  const { data: staffMembers, refetch: refetchStaff } = useQuery({
    queryKey: ["community-members"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("community_members")
        .select(`
          *,
          schools (
            name
          )
        `)
        .order("name");
      
      if (error) throw error;
      return data;
    },
  });

  const handleViewDetails = (member: CommunityMember) => {
    setSelectedMember(member);
  };

  const handleEditStaff = (member: CommunityMember) => {
    setStaffToEdit(member);
    setIsEditDialogOpen(true);
  };

  const handleDeleteStaff = (member: CommunityMember) => {
    setStaffToDelete(member);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteStaff = async () => {
    if (!staffToDelete) return;

    try {
      const { error } = await supabase
        .from("community_members")
        .delete()
        .eq("id", staffToDelete.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Staff member deleted successfully",
      });

      refetchStaff();
    } catch (error) {
      console.error("Error deleting staff member:", error);
      toast({
        title: "Error",
        description: "Failed to delete staff member",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setStaffToDelete(null);
    }
  };

  if (selectedMember) {
    return (
      <CommunityMemberDetails
        member={selectedMember}
        onBack={() => setSelectedMember(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Staff Management</h2>
        <Button onClick={() => setIsFormOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Staff Member
        </Button>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>School</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {staffMembers?.map((member) => (
              <TableRow key={member.id}>
                <TableCell>{member.name}</TableCell>
                <TableCell>{member.category}</TableCell>
                <TableCell>{member.schools?.name}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end space-x-2">
                    <Button 
                      variant="ghost"
                      onClick={() => handleViewDetails({
                        id: member.id,
                        name: member.name,
                        category: member.category,
                        school_id: member.school_id
                      })}
                    >
                      View Details
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditStaff({
                        id: member.id,
                        name: member.name,
                        category: member.category,
                        school_id: member.school_id,
                        residence: member.residence,
                        contact: member.contact
                      })}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteStaff({
                        id: member.id,
                        name: member.name,
                        category: member.category,
                        school_id: member.school_id
                      })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {isFormOpen && (
        <AddCommunityMemberDialog
          open={isFormOpen}
          onOpenChange={(open) => {
            setIsFormOpen(open);
            if (!open) refetchStaff();
          }}
        />
      )}

      {isEditDialogOpen && staffToEdit && (
        <EditStaffDialog
          open={isEditDialogOpen}
          onOpenChange={(open) => {
            setIsEditDialogOpen(open);
            if (!open) {
              refetchStaff();
              setStaffToEdit(null);
            }
          }}
          staffMember={staffToEdit}
        />
      )}

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the staff 
              member {staffToDelete?.name} and all associated records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setStaffToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteStaff}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
